import * as Yup from 'yup';
import { wave } from '@round/api';
import {
    AccountValue,
    BundleOfferValue,
    SignupFormValues,
    bundleOfferAccounts,
    isBundleOfferEmpty,
} from './SignupWizard';
import { MAX_BUNDLE_DURATION_DAYS, MIN_BUNDLE_DURATION_DAYS, MIN_BUNDLE_POSTS, accountLabel } from './steps';

// Each rule is a test against the whole row rather than a `when` between the row's own
// fields: every field's requirement depends on the others, which yup's shape resolver
// rejects as a cyclic dependency. A row nobody started stays valid — the step is optional.
const hasStarted = (row: unknown) => !isBundleOfferEmpty(row as BundleOfferValue);

const bundleOfferShape = {
    numPosts: Yup.string()
        .test('bundle-posts-required', 'Enter how many posts this bundle covers', function (value) {
            return !hasStarted(this.parent) || Boolean(value?.trim());
        })
        .test(
            'bundle-posts-whole',
            'Enter a whole number of posts',
            (value) => !value?.trim() || Number.isInteger(Number(value))
        )
        .test(
            'bundle-posts-min',
            `A bundle needs at least ${MIN_BUNDLE_POSTS} posts`,
            (value) => !value?.trim() || Number(value) >= MIN_BUNDLE_POSTS
        ),
    price: Yup.string()
        .test('bundle-price-required', 'Enter a price for this bundle', function (value) {
            return !hasStarted(this.parent) || Boolean(value?.trim());
        })
        .test('bundle-price-positive', 'Enter a valid price', (value) => !value?.trim() || Number(value) > 0),
    expectedDurationDays: Yup.string()
        .test(
            'bundle-duration-whole',
            'Enter the duration as a whole number of days',
            (value) => !value?.trim() || Number.isInteger(Number(value))
        )
        .test(
            'bundle-duration-range',
            `Pick between ${MIN_BUNDLE_DURATION_DAYS} and ${MAX_BUNDLE_DURATION_DAYS} days`,
            (value) =>
                !value?.trim() ||
                (Number(value) >= MIN_BUNDLE_DURATION_DAYS && Number(value) <= MAX_BUNDLE_DURATION_DAYS)
        ),
};

const bundleOfferAccountsShape = {
    accountUids: Yup.array().test(
        'bundle-accounts-required',
        'Pick at least one account for this bundle',
        function (value) {
            return !hasStarted(this.parent) || Boolean(value?.length);
        }
    ),
};

/** Spread into a wizard's schema shape. */
export const bundleOffersValidation = () => ({
    // The account picker is hidden with a single account, so a selection is only
    // asked for once there is something to choose between.
    bundleOffers: Yup.array().when('accounts', {
        is: (accounts: AccountValue[]) => (accounts?.length ?? 0) > 1,
        then: Yup.array().of(Yup.object().shape({ ...bundleOfferShape, ...bundleOfferAccountsShape })),
        otherwise: Yup.array().of(Yup.object().shape(bundleOfferShape)),
    }),
});

export const bundleOfferSummary = (offer: BundleOfferValue, accounts: AccountValue[]): string =>
    [
        offer.numPosts.trim() && `${offer.numPosts.trim()} posts`,
        offer.price.trim() && `${offer.price.trim()} ${offer.currency}`,
        bundleOfferAccounts(offer, accounts)
            .map((account) => accountLabel(account, accounts.indexOf(account)))
            .join(', '),
        offer.expectedDurationDays.trim() && `within ${offer.expectedDurationDays.trim()} days`,
    ]
        .filter(Boolean)
        .join(' · ');

/** Spread into a wizard's posted body. */
export const bundleOffersBody = (values: SignupFormValues): wave.CreatorApplicationBundleOfferBody[] =>
    values.bundleOffers
        .filter((offer) => !isBundleOfferEmpty(offer))
        .map((offer) => ({
            num_posts: Number(offer.numPosts),
            price: offer.price.trim(),
            currency: offer.currency,
            // Handles are trimmed exactly like the top-level `accounts` rows: the
            // backend matches a bundle's accounts on platform + normalized handle.
            // Sending every account when nothing is selected matches the backend's
            // own reading of an empty account list as "all of them".
            accounts: bundleOfferAccounts(offer, values.accounts).map((account) => ({
                platform: account.platform,
                handle: account.handle.trim(),
            })),
            // DRF parses a DurationField as "DD HH:MM:SS"; a bare number is seconds.
            expected_duration: offer.expectedDurationDays.trim()
                ? `${Number(offer.expectedDurationDays)} 00:00:00`
                : null,
        }));
